import React, { CSSProperties, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";

import cn from "classnames";

import BaseMapStyle from "@mapmycustomers/shared/enum/map/BaseMapStyle";
import { convertLatLngBoundsToPlatformBounds } from "@mapmycustomers/shared/util/geo/GeoService";
import { LoadingSpinner } from "@mapmycustomers/ui";

import FeaturedMap from "@app/component/map/FeaturedMap";
import { initializeMapView, updateViewportState } from "@app/scene/map/store/actions";
import { selectUserById } from "@app/scene/map/store/layers/userLocations/actions";
import { getMapStyle } from "@app/scene/map/store/selectors";
import { showEntityView } from "@app/store/entityView/actions";
import { RootState } from "@app/store/rootReducer";
import MapPersistedViewportState from "@app/types/map/MapPersistedViewportState";
import MapViewportState from "@app/types/map/MapViewportState";
import loggingService from "@app/util/logging";
import applyBaseMapStyle from "@app/util/map/applyBaseMapStyle";
import useHiddenChat from "@app/util/useHiddenChat";

import styles from "./MapPageMap.module.scss";
import useInitializePreviewEntity from "./util/useInitializePreviewEntity";
import useInitializeUserLocations from "./util/useInitializeUserLocations";

interface Props {
  children: ReactNode;
  className?: string;
  initialize: (callback: (viewport: MapPersistedViewportState) => void) => void;
  mapClassName?: string;
  mapStyle: BaseMapStyle;
  onViewportChange?: (viewport: MapViewportState) => void;
  selectUserById: typeof selectUserById;
  showEntityView: typeof showEntityView;
  style?: CSSProperties;
}

const MapPageMap: React.FC<Props> = ({
  children,
  className,
  initialize,
  mapClassName,
  mapStyle,
  onViewportChange,
  selectUserById,
  showEntityView,
  style,
}) => {
  // not only defines initial viewport state, but also works as an `initialized` flag
  const [viewportState, setViewportState] = useState<MapPersistedViewportState | undefined>();
  const mapRef = useRef<google.maps.Map | undefined>(undefined);

  useEffect(() => {
    initialize(setViewportState);
  }, [initialize]);

  useHiddenChat();

  const initializePreviewEntity = useInitializePreviewEntity(showEntityView);
  const initializeUserLocations = useInitializeUserLocations(selectUserById);

  const handleMapInitialized = useCallback(
    (map) => {
      loggingService.debug("map initialized in MapPageMap");
      const bounds = map.getBounds();
      const zoom = map.getZoom();
      if (bounds && zoom && onViewportChange) {
        // we need to initialize store viewport with the current bounds
        onViewportChange({ bounds: convertLatLngBoundsToPlatformBounds(bounds), zoom });
      }
      applyBaseMapStyle(map, mapStyle);
      mapRef.current = map;
      initializePreviewEntity();
      initializeUserLocations();
    },
    [initializePreviewEntity, initializeUserLocations, mapStyle, onViewportChange]
  );

  useEffect(() => {
    if (mapRef.current) {
      applyBaseMapStyle(mapRef.current, mapStyle);
    }
  }, [mapStyle]);

  return !viewportState ? (
    <LoadingSpinner global />
  ) : (
    <FeaturedMap
      className={cn(styles.container, className)}
      initialOptions={{
        center: viewportState.center
          ? { lat: viewportState.center[1], lng: viewportState.center[0] }
          : { lat: 0, lng: 0 },
        clickableIcons: false,
        controlSize: 32,
        disableDefaultUI: true,
        gestureHandling: "greedy",
        zoom: viewportState.zoom ?? 2,
      }}
      mapClassName={cn(styles.map, mapClassName)}
      onInitialized={handleMapInitialized}
      style={style}
    >
      {children}
    </FeaturedMap>
  );
};

const mapStateToProps = (state: RootState) => ({
  mapStyle: getMapStyle(state),
});

const mapDispatchToProps = {
  initialize: initializeMapView.request,
  onViewportChange: updateViewportState,
  selectUserById,
  showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(MapPageMap);
